// src/hooks/metrics/useValuationMetrics.ts
/**
 * Derived valuation metrics hooks - compose TokenFundamentalsContext with metrics library
 * 
 * These hooks provide computed valuation metrics that depend on fundamental data.
 * They use the centralized metrics library for calculations.
 * 
 * Usage:
 *   const { feesCv, priceFeesCorr, mcFeesZScore } = useValuationMetrics(90);
 */

import { useMemo } from 'react';
import { useTokenFundamentals, useTokenPrice } from '@/contexts/token';
import {
  cvPercent,
  zScore,
  percentileRank,
  correlation,
  filterValid,
} from '@/lib/metrics';

// ============================================================================
// VALUATION METRICS HOOK
// ============================================================================

export interface ValuationMetricsResult {
  /** Fee yield = 1 / MC/Fees (as percentage) */
  feeYieldPct: number | null;
  /** Fees to TVL ratio (as percentage) */
  feesToTvlPct: number | null;
  /** Fees coefficient of variation (stability) */
  feesCvPct: number | null;
  /** TVL coefficient of variation */
  tvlCvPct: number | null;
  /** MC/Fees z-score vs history */
  mcFeesZScore: number | null;
  /** MC/TVL z-score vs history */
  mcTvlZScore: number | null;
  /** Price-Fees correlation */
  priceFeesCorr: number | null;
  /** Price-TVL correlation */
  priceTvlCorr: number | null;
  /** MC/Fees momentum (% change over window) */
  mcFeesMomentumPct: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate derived valuation metrics
 * @param window - Number of days to use for calculations
 */
export function useValuationMetrics(window: number = 90): ValuationMetricsResult {
  const { 
    fees, 
    tvl, 
    mcToFeesHistory, 
    mcToTvlHistory,
    current, 
    isLoading: fundamentalsLoading 
  } = useTokenFundamentals();
  
  const { token, isLoading: priceLoading } = useTokenPrice();
  const prices = token.prices;
  
  return useMemo(() => {
    const isLoading = fundamentalsLoading || priceLoading;
    
    if (isLoading) {
      return {
        feeYieldPct: null,
        feesToTvlPct: null,
        feesCvPct: null,
        tvlCvPct: null,
        mcFeesZScore: null,
        mcTvlZScore: null,
        priceFeesCorr: null,
        priceTvlCorr: null,
        mcFeesMomentumPct: null,
        isLoading,
      };
    }
    
    // Fee yield = 1 / MC/Fees
    const feeYieldPct = current.mcToFees != null && current.mcToFees !== 0
      ? (1 / current.mcToFees) * 100
      : null;
    
    // Fees to TVL = 1 / TVL/Fees
    const feesToTvlPct = current.tvlToFees != null && current.tvlToFees !== 0
      ? (1 / current.tvlToFees) * 100
      : null;
    
    // Window slices
    const feesSlice = fees.values.slice(-window);
    const tvlSlice = tvl.values.slice(-window);
    const mcFeesSlice = mcToFeesHistory.values.slice(-window);
    const mcTvlSlice = mcToTvlHistory.values.slice(-window);
    const priceSlice = prices.values.slice(-window);
    
    // Coefficient of variation (stability)
    const feesCvPct = cvPercent(feesSlice);
    const tvlCvPct = cvPercent(tvlSlice);
    
    // Z-scores for current ratios vs historical
    const mcFeesZScore = current.mcToFees != null
      ? zScore(current.mcToFees, mcFeesSlice)
      : null;
    
    const mcTvlZScore = current.mcToTvl != null
      ? zScore(current.mcToTvl, mcTvlSlice)
      : null;
    
    // Price correlations
    const validFees = filterValid(feesSlice);
    const validTvl = filterValid(tvlSlice);
    const validPrices = filterValid(priceSlice);
    
    // Align for correlation
    const n = Math.min(validFees.length, validPrices.length);
    const priceFeesCorr = n >= 5 
      ? correlation(validPrices.slice(-n), validFees.slice(-n))
      : null;
    
    const n2 = Math.min(validTvl.length, validPrices.length);
    const priceTvlCorr = n2 >= 5
      ? correlation(validPrices.slice(-n2), validTvl.slice(-n2))
      : null;
    
    // MC/Fees momentum
    const validMcFees = filterValid(mcFeesSlice);
    let mcFeesMomentumPct: number | null = null;
    if (validMcFees.length > 1) {
      const curr = validMcFees[validMcFees.length - 1]!;
      const prev = validMcFees[0]!;
      if (prev !== 0) {
        mcFeesMomentumPct = ((curr - prev) / prev) * 100;
      }
    }
    
    return {
      feeYieldPct,
      feesToTvlPct,
      feesCvPct,
      tvlCvPct,
      mcFeesZScore,
      mcTvlZScore,
      priceFeesCorr,
      priceTvlCorr,
      mcFeesMomentumPct,
      isLoading,
    };
  }, [
    fees.values, 
    tvl.values, 
    mcToFeesHistory.values, 
    mcToTvlHistory.values,
    prices.values,
    current, 
    window, 
    fundamentalsLoading,
    priceLoading,
  ]);
}

// ============================================================================
// SECTOR PERCENTILE HOOK
// ============================================================================

export interface SectorPercentileResult {
  /** MC/Fees percentile within sector */
  mcFeesPercentile: number | null;
  /** MC/TVL percentile within sector */
  mcTvlPercentile: number | null;
  /** Loading state */
  isLoading: boolean;
}

/**
 * Calculate percentile rankings within a sector
 * @param sectorRatios - Array of sector peer ratios
 */
export function useSectorPercentiles(
  sectorRatios: { mcToFees: number | null; mcToTvl: number | null }[]
): SectorPercentileResult {
  const { current, isLoading } = useTokenFundamentals();
  
  return useMemo(() => {
    if (isLoading || sectorRatios.length === 0) {
      return {
        mcFeesPercentile: null,
        mcTvlPercentile: null,
        isLoading,
      };
    }
    
    const mcFeesSample = sectorRatios
      .map(r => r.mcToFees)
      .filter((v): v is number => v != null && Number.isFinite(v));
    
    const mcTvlSample = sectorRatios
      .map(r => r.mcToTvl)
      .filter((v): v is number => v != null && Number.isFinite(v));
    
    const mcFeesPercentile = current.mcToFees != null
      ? percentileRank(current.mcToFees, mcFeesSample)
      : null;
    
    const mcTvlPercentile = current.mcToTvl != null
      ? percentileRank(current.mcToTvl, mcTvlSample)
      : null;
    
    return {
      mcFeesPercentile,
      mcTvlPercentile,
      isLoading,
    };
  }, [current, sectorRatios, isLoading]);
}
